import { useState, useCallback, useRef, useContext } from 'react';
import addUniqueIDstoList from '../utils/addUniqueIDs';
import { unitParamMap } from '../utils/unitHelpers';
import { FacilityUnitContext } from '../context';
import { serviceLogger } from '../utils/serviceLogger';
import _, { isEqual } from 'lodash';

/**
 * Custom hook to fetch paginated data with dynamic parameters and context-based filtering.
 * This hook is designed for endpoints that support server-side pagination.
 *
 * @param {Function} fetchFunction - The API function to fetch data (expects `page` and `page_size` parameters).
 * @param {Object} extraParams - Additional parameters for the API request.
 * @param {number} initialPageSize - Number of items per page.
 * @returns {Object} - Returns:
 *   - `data`: Accumulated data from all API pages.
 *   - `loading`: Loading state.
 *   - `error`: Error state.
 *   - `fetchData`: Function to fetch the next page of data.
 *   - `page`: Current page fetched from the API.
 */
const useFetchPaginatedData = (
  fetchFunction,
  extraParams = {},
  initialPageSize = 10,
  newCurrentPage,
  fetchFunctionWithFilters,
  dataType,
) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(newCurrentPage);
  const [totalItems, setTotalItems] = useState(1);

  const { selectedUnit } = useContext(FacilityUnitContext);
  const extraParamsRef = useRef(extraParams);
  if (!isEqual(extraParams, extraParamsRef.current)) {
    extraParamsRef.current = extraParams;
  }

  const getResultsList = (result) => {
    if (_.isArray(result?.results)) return result.results;
    if (result?.items) return [result];
    return result;
  };

  const fetchDataWithFilters = useCallback(async () => {
    try {
      setLoading(true);
      const result = await fetchFunctionWithFilters(extraParamsRef.current.metric_id);
      const resultWithId = addUniqueIDstoList([result]);
      setData(resultWithId);
      setPage(1);
      setTotalItems(1);
    } catch (err) {
      serviceLogger.error('Error fetching location data:', err);
      setError('Failed to data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [extraParamsRef, fetchFunctionWithFilters]);

  const fetchData = useCallback(
    async (emptyData = false) => {
      setLoading(true);
      if (emptyData) setData([]);

      if (!selectedUnit || !fetchFunction) return;

      try {
        setError(null);

        const getUnitParam = unitParamMap[dataType];
        const unitParam = getUnitParam(selectedUnit);

        const result = await fetchFunction(
          unitParam,
          newCurrentPage,
          initialPageSize,
          extraParamsRef.current,
        );

        const resultsList = getResultsList(result);

        const resultWithId = addUniqueIDstoList(resultsList);

        setData(resultWithId);
        setPage(result?.page);
        setTotalItems(result?.count || result?.total);
      } catch (err) {
        serviceLogger.error('Error fetching data:', err);
        setError('Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    },
    [fetchFunction, newCurrentPage, initialPageSize, selectedUnit, dataType],
  );

  return {
    data,
    loading,
    error,
    fetchData,
    page,
    totalItems,
    fetchDataWithFilters,
  };
};

export default useFetchPaginatedData;
